import ToggleResourceMobileMultiselectButton from '@/src/components/SelectionButton/SelectionButton';
import { ZoomSliderWithinDropdownV2 } from '@/src/components/ZoomSlider/ZoomSliderWithinDropdown';
import { DashboardHeader } from '@fabric-app/business/dashboard/components/DashboardHeader';
import { DashboardHeaderFilterSortResources } from '@fabric-app/business/dashboard/components/DashboardHeaderFilterSortResources';
import useOpenInTabs from '@/src/hooks/useOpenInTabs';
import BackButton from '@/src/modules/backNavigation/components/BackButton';
import useUIStore from '@/src/store/ui';
import type { OptimisticDraft } from '@/src/types/draftable';
import { shallow } from 'zustand/shallow';
import { captureException } from '@sentry/nextjs';
import type { ResourceDetail } from '@fabric-app/types/resources';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { useQueryResourceSummary } from '@fabric-app/business/resources/queries/useQueryResourceSummary';
import { useFilterToggleButton } from '@fabric-app/business/hooks/useFilterToggleButton';
import { useQueryFolder } from '@fabric-app/business/resources/queries/useQueryFolder';
import { useResourcePermissionsByCurrentUser } from '@fabric-app/business/resource-detail/components/context/getDisabledActionsReasons';
import { useContextShareToken } from '@fabric-app/business/permission/contexts/AnonShareTokenContext';
import { pick } from '@fabric-app/utils/object/manipulation';
import { resourceSharingModal } from '@fabric-app/business/permission/components/ModalShare/resourceSharingStore';
import { MemberBarSpaceOrFolderWithLiveBlocks } from '@fabric-app/business/multiplayer/components/MemberBar/MemberBarSpaceOrFolderWithLiveBlocks';
import Skeleton from '@fabric-app/ui/components/Skeleton';
import { BadgePermissionRole } from '@fabric-app/business/permission/components/BadgePermissionRole';
import { DropdownMenu } from '@fabric-app/ui/components/DropdownMenu/DropdownMenu';
import {
  IconFolderCreate,
  IconLinkWindow,
  IconPlusSmall,
  IconUserPlus,
} from '@fabric-app/icons/svg';
import { DropdownItemExport } from '@fabric-app/business/export/components/DropdownItemExport';
import { DropdownItemDeleteLeave } from './DropdownItemDeleteLeave';
import { ResourceUiDropdownShowControls } from '@fabric-app/business/resources/components/DropdownShowControls';
import { ResourceTopBarPath } from '@fabric-app/business/resources/components/ResourceTopBarPath';
import { useModalNewResourceContext } from '@fabric-app/business/resources/components/NewResource/ModalNewResourceContext';

import { PermissionRole } from '@fabric-app/types/permission';
import { NoSsr } from '@fabric-app/ui/components/NoSSR';
import { BadgeCount } from '@fabric-app/ui/components/BadgeCount';
import { AddToLibraryButton } from '@fabric-app/business/spaces/components/AddToLibraryButton';
import { NewResourceType } from '@fabric-app/business/resources/components/NewResource/config';
import { useResourceListPreferencesContext } from '@fabric-app/business/resources/components/ResourceList/contexts/resourceListContextPreferences';
import { ViewModeSwitcher } from '@fabric-app/business/resources/components/ResourceList/ViewModeSwitcher/ViewModeSwitcher';
import { DropdownItemPinToHome } from './DropdownItemPinToHome';
import { HomeButton } from '@/src/modules/publishing/components/HomeButton';

/**
 * there's probably too much prop passing but i think it's alright because this component won't be used anywhere else
 * Though we could still look for some code to be transfered from the main component to heres
 * @returns
 */
export const SpaceHeader: React.FC<{
  listId: string;
  onClickActivtyToggle: VoidFunction;
  resources: OptimisticDraft<ResourceDetail>[];
  onBackClick?: VoidFunction | null;
  onClickFolder?: (resourceFolder: string) => void;
}> = ({ listId, resources, onBackClick, onClickFolder }) => {
  const { isMobileView } = useResponsive();
  const shareToken = useContextShareToken();
  const accessToken = shareToken?.accessToken;
  const { data: totals } = useQueryResourceSummary(listId);
  const { element: filterToggleButtonEl, ...filterVisibilityState } = useFilterToggleButton();

  const { folder } = useQueryFolder({ resourceId: listId });

  /**
   * permission related stuff
   */
  const { disabledActions, userRole } = useResourcePermissionsByCurrentUser(folder);

  const { fabricExtensionInstalled, openInTabs } = useOpenInTabs();

  const { viewMode } = useResourceListPreferencesContext();

  const { zoomLevel, setZoomLevel } = useUIStore(
    (state) => pick(state, ['zoomLevel', 'setZoomLevel']),
    shallow,
  );

  const { setNewModalOpen } = useModalNewResourceContext();

  const handleCreateNewResource = () => {
    setNewModalOpen(true);
  };

  const onOpenShareModal = () => resourceSharingModal.open({ resourceId: listId });

  const sharedViewportActionButtons = (
    <MemberBarSpaceOrFolderWithLiveBlocks
      type="space"
      resourceId={listId}
      onOpenShareModal={disabledActions.manageSomePermissionRoles ? undefined : onOpenShareModal}
      renderShareButtonOutside
      isPublicShared={folder?.isShared || false}
    />
  );

  const handleCreateSubFolder = () => {
    /**
     * should be already preloaded
     */
    if (folder) {
      setNewModalOpen(true, {
        type: NewResourceType.Folder,
        parent: {
          id: folder?.id,
          name: folder?.name,
        },
      });
    } else {
      captureException(new Error('Create folder (space header) List not found'));
    }
  };

  const dropdownMenu = !!folder && (
    <DropdownMenu.Base
      renderTriggerElement={({ onOpenChange }) => (
        <DropdownMenu.TriggerButtonThreeDots onOpenChange={onOpenChange} />
      )}
    >
      {!accessToken && (
        <>
          <DropdownMenu.Group>
            <DropdownMenu.Item
              iconElement={<IconPlusSmall />}
              onSelect={handleCreateNewResource}
              data-testid="space-add-items-link"
              disabledReason={disabledActions.createChildResources}
            >
              Add items...
            </DropdownMenu.Item>
            <DropdownMenu.Item
              iconElement={<IconFolderCreate />}
              onSelect={handleCreateSubFolder}
              data-testid="fdoc-list-context-menu-create-subfolder"
              disabledReason={disabledActions.createChildResources}
            >
              Create subfolder
            </DropdownMenu.Item>
            {isMobileView && (
              <DropdownMenu.Item
                iconElement={<IconUserPlus />}
                onClick={onOpenShareModal}
                data-testid="space-view-share-button"
                disabledReason={disabledActions.manageSomePermissionRoles}
              >
                {/** TODO manage sharing / share */}
                Manage sharing
              </DropdownMenu.Item>
            )}
            <DropdownItemPinToHome resource={folder} />
            {fabricExtensionInstalled && (
              <DropdownMenu.Item
                onSelect={() => openInTabs(resources, { newWindow: true })}
                data-testid="space-delete-button"
                iconElement={<IconLinkWindow />}
              >
                Open all in new window
              </DropdownMenu.Item>
            )}
          </DropdownMenu.Group>
        </>
      )}
      <DropdownMenu.Group>
        <DropdownItemExport
          label="Export space content"
          resourceId={listId}
          toastProps={{ content: "Exporting this space's content..." }}
        />
      </DropdownMenu.Group>
      {!accessToken && <DropdownItemDeleteLeave currentUserRole={userRole} spaceId={listId} />}
      {!isMobileView && ['Grid', 'Masonry'].includes(viewMode) && (
        <>
          <DropdownMenu.Separator />
          <DropdownMenu.Group>
            <ResourceUiDropdownShowControls uiContext="global" />
          </DropdownMenu.Group>
        </>
      )}

      <ZoomSliderWithinDropdownV2 onChange={setZoomLevel} value={zoomLevel} viewMode={viewMode} />
    </DropdownMenu.Base>
  );

  return (
    <>
      <DashboardHeader data-multiselect-clear>
        <DashboardHeader.Body>
          <DashboardHeader.Left>
            {isMobileView && !accessToken && onBackClick !== null && (
              <BackButton onClick={onBackClick} />
            )}
            {!isMobileView && (
              <NoSsr>
                <HomeButton />
                <AddToLibraryButton type="space" resource={folder} />
              </NoSsr>
            )}
            {folder ? (
              <>
                <ResourceTopBarPath resource={folder} onClickFolder={onClickFolder} />
                {totals?.total ? (
                  <NoSsr>
                    <BadgeCount>{totals.total}</BadgeCount>
                  </NoSsr>
                ) : null}
              </>
            ) : (
              <Skeleton width={200} height={30} borderRadius={30} />
            )}
            {folder && userRole !== PermissionRole.OWNER && shareToken?.type !== 'publishing' && (
              <BadgePermissionRole role={userRole} itemType="space" />
            )}
          </DashboardHeader.Left>
          {!isMobileView && (
            <DashboardHeader.Right>
              {filterToggleButtonEl}

              <ViewModeSwitcher />
              {/** new and plus, displayed on non-mobile only. On mobile, there's + in the footer */}
              {sharedViewportActionButtons}
              {/** tripple dot menu */}
              {dropdownMenu}
            </DashboardHeader.Right>
          )}
        </DashboardHeader.Body>
        {isMobileView && (
          <DashboardHeader.Body>
            <DashboardHeader.Left>
              {!disabledActions.modifyChildResources && <ToggleResourceMobileMultiselectButton />}
              {filterToggleButtonEl}
              {sharedViewportActionButtons}
            </DashboardHeader.Left>
            <DashboardHeader.Right>
              <NoSsr>
                <AddToLibraryButton type="space" resource={folder} />
              </NoSsr>
              {dropdownMenu}
            </DashboardHeader.Right>
          </DashboardHeader.Body>
        )}
      </DashboardHeader>

      <DashboardHeaderFilterSortResources
        visible={filterVisibilityState.value}
        resourceTotals={totals}
        resourceId={folder?.id}
      />
    </>
  );
};
